import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { ColDef, ColumnResizedEvent, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';

// EZ
import { EzBaseComponent } from '@template-ui/components/ez-base.component';
import { DocMeta } from '@template-ui/decorators';
import { UiLibraryModule } from '@template-ui/template-ui.module';
import { PanelHeaderConfig } from '@template-ui/types';

/** Row data type for the space-missions sample grid. */
type SpaceMission = {
  mission: string;
  agency: string;
  destination: string;
  year: number;
  crew: number;
};

/** Static sample data — notable space missions. */
const SPACE_MISSIONS: SpaceMission[] = [
  { mission: 'Apollo 11', agency: 'NASA', destination: 'Moon', year: 1969, crew: 3 },
  { mission: 'Vostok 1', agency: 'Roscosmos', destination: 'Earth Orbit', year: 1961, crew: 1 },
  { mission: 'Voyager 1', agency: 'NASA', destination: 'Interstellar Space', year: 1977, crew: 0 },
  { mission: 'Mars Pathfinder', agency: 'NASA', destination: 'Mars', year: 1997, crew: 0 },
  { mission: 'Hubble ST Deployment', agency: 'NASA / ESA', destination: 'Low Earth Orbit', year: 1990, crew: 5 },
  { mission: 'Rosetta', agency: 'ESA', destination: 'Comet 67P', year: 2004, crew: 0 },
  { mission: 'Crew Dragon Demo-2', agency: 'SpaceX / NASA', destination: 'ISS', year: 2020, crew: 2 },
  { mission: 'Artemis I', agency: 'NASA', destination: 'Lunar Orbit', year: 2022, crew: 0 },
];

/**
 * Showcase sample demonstrating `ezHeaderName` on EZ Grid column definitions.
 *
 * Covers four usage modes:
 * - **HTML string** — rendered safely via `EzSafeHtmlPipe`
 * - **Reactive signal** — header text updates live when the signal changes
 * - **ezText template** — `{{field|titleCase}}` resolved against column metadata; `{{unit}}` sourced from `agGridOptions.context`
 * - **Standard `headerName`** — plain AG Grid property, fully compatible alongside `ezHeaderName`
 */
@DocMeta({
  hide: true,
  category: 'Components',
  showcaseTitle: 'EZ Grid Header',
  showcaseDesc: "This is not used because there is a companion ez-grid-header-sample.component.md file. That will be used instead.",
  showcaseAttachTo: [{ libName: 'Base Library', classNames: ['EzGridComponent'] }],
})
@Component({
  selector: 'ui-grid-header-sample, ez-grid-header-sample',
  standalone: true,
  imports: [CommonModule, UiLibraryModule],
  templateUrl: './ez-grid-header-sample.component.html',
  styleUrl: './ez-grid-header-sample.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EzGridHeaderSampleComponent extends EzBaseComponent {
  // ───────────────────────────────────────────────────────────────
  //  Private/Internal State
  // ───────────────────────────────────────────────────────────────

  /** Tracks the current pixel width of the agency column, updated via onColumnResized. */
  private readonly agencyColWidth = signal(150);

  /** AG Grid API, captured at grid ready to allow column queries outside of resize events. */
  private gridApi?: GridApi;


  // ───────────────────────────────────────────────────────────────
  //  Computed Signals / Derived State
  // ───────────────────────────────────────────────────────────────

  /** Reactive header label for the agency column — switches to short form when the column is narrow. */
  protected readonly agencyHeaderSig = computed(() =>
    this.agencyColWidth() > 120 ? `Space Agency (width: <b class="text-blue-400">${this.agencyColWidth()}</b>)` : 'Agency'
  );

  /** Subtitle shown in the panel header, reflecting current agency column width. */
  private readonly panelSubtitle = computed(() =>
    `Agency column: ${this.agencyColWidth()}px → "${this.agencyHeaderSig()}"`
  );

  // ───────────────────────────────────────────────────────────────
  //  Public Template-Bound Properties
  // ───────────────────────────────────────────────────────────────

  /** Static row data passed directly to the grid via `[rowData]`. */
  protected readonly missions: SpaceMission[] = SPACE_MISSIONS;

  /**
   * Grid options — enables column resizing and tracks the agency column width so the
   * reactive header can switch between "Space Agency" and "Agency".
   * `context` supplies custom tokens for ezText header templates.
   */
  protected readonly agGridOptions: GridOptions = {
    context: { crewUnit: 'astronauts' },

    onGridReady: (event: GridReadyEvent) => {
      this.gridApi = event.api;
    },

    onColumnResized: (_event: ColumnResizedEvent) => {
      const width = this.gridApi?.getColumn('agency')?.getActualWidth();
      if (width !== undefined) {
        this.agencyColWidth.set(width);
      }
    },
  };

  /** Override defaultColDef to enable column resizing in this sample. */
  protected readonly sampleDefaultColDef: ColDef = { resizable: true };

  protected readonly panelHeaderConfig: PanelHeaderConfig = {
    panelHeaderTitle: 'Space Missions',
    subtitle: this.panelSubtitle,
  };

  protected readonly colDefs: ColDef<SpaceMission>[] = [
    {
      field: 'mission',
      // HTML header — static markup rendered via EzSafeHtmlPipe
      ezHeaderName: '<strong>Mission</strong> 🚀 (this uses <i class="text-blue-400">HTML</i>)',       // HTML is sanitized and rendered
      //headerName: '<strong>Mission</strong> 🚀 yuck',  // <-- won't work, you'll see the raw HTML
      minWidth: 180,
    },
    {
      field: 'agency',
      // Signal header — live-updates as the column is resized (try dragging the column edge)
      ezHeaderName: this.agencyHeaderSig,
      resizable: true,
      minWidth: 80,
    },
    {
      field: 'destination',
      // Demonstrate the context
      ezHeaderName: `field: <b>{{field}}</b>, colId: <b>{{colId}}</b>, diplayName: <b>{{displayName}}</b>`
    },
    {
      field: 'year',
      // Standard AG Grid headerName — no ezHeaderName needed
      headerName: 'Launch Year',
      maxWidth: 130,
    },
    {
      field: 'crew',
      // ezText template — {{field|titleCase}} uses column metadata; {{crewUnit}} comes from agGridOptions.context
      ezHeaderName: '{{field|titleCase}} ({{crewUnit}})',
      maxWidth: 160,
    },
  ];

  // ───────────────────────────────────────────────────────────────
  //  Lifecycle Hooks
  // ───────────────────────────────────────────────────────────────

  constructor() {
    super();
    this.thisClassName = 'EzGridHeaderSampleComponent';
  }

  // ───────────────────────────────────────────────────────────────
  //  Public Methods (Template-Bound Handlers)
  // ───────────────────────────────────────────────────────────────

}

